import { useState } from "react"

import { useCombobox } from "downshift"

import { Users, X } from "lucide-react"

import {
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
} from "@/components/ui/Field"
import { Spinner } from "@/components/ui/Spinner"

import { isValidEmail } from "../utils/email"

// ==================== Types ====================

export interface ColleagueSuggestion {
  email: string
  name: string
}

interface ColleagueInputProps {
  invitedColleagues: string[]
  onAdd: (email: string) => void
  onRemove: (email: string) => void
  onSearch: (query: string) => ColleagueSuggestion[]
  error?: string
  label?: string
  placeholder?: string
  description?: string
}

// ==================== Component ====================

export function ColleagueInput({
  invitedColleagues,
  onAdd,
  onRemove,
  onSearch,
  error,
  label = "Add Team Members",
  placeholder = "Search by name or enter email...",
  description = "Press Enter to add, or select from suggestions",
}: ColleagueInputProps) {
  const [inputValue, setInputValue] = useState("")
  const [suggestions, setSuggestions] = useState<ColleagueSuggestion[]>([])
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false)

  // ==================== Handlers ====================

  function handleInputValueChange(value: string) {
    setInputValue(value)

    if (value.length === 0) {
      setSuggestions([])
      setIsLoadingSuggestions(false)
      return
    }

    setIsLoadingSuggestions(true)
    const results = onSearch(value)
    setSuggestions(results)
    setIsLoadingSuggestions(false)
  }

  function handleAddColleague(email: string) {
    const trimmedEmail = email.trim()
    if (
      trimmedEmail &&
      isValidEmail(trimmedEmail) &&
      !invitedColleagues.includes(trimmedEmail)
    ) {
      onAdd(trimmedEmail)
      setInputValue("")
      setSuggestions([])
    }
  }

  function handleSelection(selectedItem: ColleagueSuggestion | null) {
    if (selectedItem) {
      handleAddColleague(selectedItem.email)
    }
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter" && inputValue) {
      if (!isOpen || suggestions.length === 0) {
        event.preventDefault()
        handleAddColleague(inputValue)
      }
    }
  }

  // ==================== Downshift Setup ====================

  const {
    isOpen,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
    reset,
  } = useCombobox({
    items: suggestions,
    inputValue,
    onInputValueChange: ({ inputValue }) => {
      handleInputValueChange(inputValue || "")
    },
    onSelectedItemChange: ({ selectedItem }) => {
      handleSelection(selectedItem)
      reset()
    },
    itemToString: (item) => (item ? item.email : ""),
  })

  // ==================== Render Helpers ====================

  function renderInputField() {
    return (
      <Field data-invalid={!!error} className="tw-space-y-2">
        <FieldLabel
          htmlFor="colleague"
          className="tw-block tw-text-sm tw-font-semibold tw-text-gray-700"
        >
          {label} <span className="tw-text-red-500">*</span>
        </FieldLabel>
        <div className="tw-relative">
          <input
            {...getInputProps({
              id: "colleague",
              placeholder,
              onKeyDown: handleKeyDown,
            })}
            className={`tw-w-full tw-px-4 tw-py-3 tw-border tw-rounded-sm tw-text-sm tw-text-gray-900 tw-transition-all focus:tw-outline-none focus:tw-ring-2 ${
              error
                ? "tw-border-red-300 tw-bg-red-50 focus:tw-ring-red-500"
                : "tw-border-gray-300 focus:tw-border-primary focus:tw-ring-primary"
            }`}
          />

          <div
            {...getMenuProps()}
            className={`tw-absolute tw-z-10 tw-w-full tw-mt-2 tw-bg-white tw-border tw-border-gray-200 tw-rounded-sm tw-shadow-xl tw-max-h-60 tw-overflow-y-auto ${
              isOpen && (suggestions.length > 0 || isLoadingSuggestions)
                ? ""
                : "tw-hidden"
            }`}
          >
            {isLoadingSuggestions ? (
              <div className="tw-flex tw-items-center tw-justify-center tw-px-4 tw-py-3">
                <Spinner />
              </div>
            ) : (
              isOpen &&
              suggestions.map((suggestion, index) => (
                <div
                  key={suggestion.email}
                  {...getItemProps({ item: suggestion, index })}
                  className={`tw-w-full tw-px-4 tw-py-3 tw-text-left tw-transition-colors tw-border-b tw-border-gray-100 last:tw-border-b-0 tw-cursor-pointer ${
                    highlightedIndex === index
                      ? "tw-bg-gray-200"
                      : "tw-bg-white hover:tw-bg-gray-50"
                  }`}
                >
                  <div className="tw-font-semibold tw-text-gray-900 tw-text-sm">
                    {suggestion.name}
                  </div>
                  <div className="tw-text-xs tw-text-gray-600 tw-mt-0.5">
                    {suggestion.email.toLowerCase()}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
        <FieldDescription className="tw-text-xs tw-text-gray-500">
          {description}
        </FieldDescription>
        {error && (
          <FieldError className="tw-text-red-600 tw-text-sm tw-flex tw-items-center tw-gap-1">
            <X className="tw-w-4 tw-h-4" />
            {error}
          </FieldError>
        )}
      </Field>
    )
  }

  function renderInvitedList() {
    return (
      <div className="tw-space-y-3">
        <div className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-semibold tw-text-gray-700">
          <Users className="tw-w-4 tw-h-4" />
          Invited ({invitedColleagues.length})
        </div>
        <div className="tw-space-y-2 tw-max-h-64 tw-overflow-y-auto tw-pr-2">
          {invitedColleagues.map((email) => (
            <div
              key={email}
              className="tw-flex tw-items-center tw-justify-between tw-bg-gray-50 tw-px-4 tw-py-3 tw-rounded-sm tw-border tw-border-primary tw-transition-all hover:tw-shadow-sm"
            >
              <span className="tw-text-sm tw-text-gray-900 tw-truncate tw-flex-1 tw-font-medium">
                {email.toLowerCase()}
              </span>
              <button
                type="button"
                onClick={() => onRemove(email)}
                className="tw-ml-3 tw-text-gray-400 hover:tw-text-red-600 tw-transition-colors tw-flex-shrink-0"
                aria-label={`Remove ${email}`}
              >
                <X className="tw-w-5 tw-h-5" />
              </button>
            </div>
          ))}
        </div>
      </div>
    )
  }

  function renderEmptyState() {
    return (
      <div className="tw-text-center tw-py-12 tw-px-4 tw-bg-gray-50 tw-rounded-xl tw-border-2 tw-border-dashed tw-border-gray-300">
        <Users className="tw-w-12 tw-h-12 tw-mx-auto tw-text-gray-400 tw-mb-3" />
        <p className="tw-text-sm tw-font-semibold tw-text-gray-600 tw-mb-1">
          No colleagues invited yet
        </p>
        <p className="tw-text-xs tw-text-gray-500">
          Start typing above to search and add team members
        </p>
      </div>
    )
  }

  // ==================== Main Render ====================

  return (
    <div className="tw-space-y-4">
      {renderInputField()}

      <div className="tw-min-h-[200px]">
        {invitedColleagues.length > 0
          ? renderInvitedList()
          : renderEmptyState()}
      </div>
    </div>
  )
}
